import DashboardContext from '@/contexts/DashboardContext';
import HydraulicSimulatorService from '@/domain/HydraulicSimulator/HydraulicSimulatorService';
import MmsService from '@/domain/Mms/MmsService';
import { mmsEventsService } from '@/events/mms.subject';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Input, Textarea, Tooltip } from '@hydrodigital/design-system';
import { Upload } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const mmsService = new MmsService();
const hydraulicSimulatorService = new HydraulicSimulatorService();
const TreeFiles = React.forwardRef(({ getModelData }, ref) => {
	const { value: context } = useContext(DashboardContext);
	const subscriptionMMS$ = mmsEventsService.getSubject();
	const [files, setFiles] = useState([]);
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedFile, setSelectedFile] = useState(null);
	const [alias, setAlias] = useState('');
	const [description, setDescription] = useState('');
	const [uploadedFile, setUploadedFile] = useState(null);

	const getTreeData = async () => {
		if (!context.selectedLocation) return;
		const response = await mmsService.getModelVersions(context.selectedLocation);
		if (response) {
			setFiles(response.model_version_network);
		}
	};

	const renderTree = (parentId) => {
		return files
			.filter((file) => {
				// Handle empty string as null for root nodes
				const fileParent = file.parent_model_id === '' ? null : file.parent_model_id;
				return fileParent === parentId;
			})
			.map((file) => (
				<li
					key={file.model_id}
					style={{
						listStyle: 'none',
						marginTop: '8px',
						marginBottom: parentId ? '8px' : '30px',
					}}
				>
					<details style={{ cursor: 'pointer', width: 'fit-content' }}>
						<summary
							style={{
								fontWeight: 'normal',
								color: '#2c3e50',
								width: 'fit-content',
								padding: '5px',
								...(file.model_id == selectedFile ? { backgroundColor: '#b9dfea' } : {}),
							}}
						>
							<Tooltip 
								text={`${file.description} - ${new Date(file.date).toLocaleDateString()}`}
								position="up"
								trigger="hover"
							>
								<div>
									<span
										style={{
											width: '150px',
											display: '-webkit-inline-box',
											textOverflow: 'ellipsis',
											overflow: 'hidden',
										}}
										className={`${file.lastest ? 'font-bold' : ''}`}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setSearchParams({
												mmsId: file.model_id,
												fileName: file.alias,
											});
											setSelectedFile(file.model_id);
										}}
									>
										{file.alias} ({file.model_id ? file.model_id.substring(0, 8) : ''})
									</span>
								</div>
							</Tooltip>
						</summary>
						<ul style={{ paddingLeft: '20px', borderLeft: '1px solid #ccc' }}>
							{renderTree(file.model_id)}
						</ul>
					</details>
				</li>
			));
	};

	const getMmsId = async ({ file, fileName, alias, description }) => {
		const security = localStorage.getItem('security');
		const organizationId = JSON.parse(security || '{}').organizationId;
		const response = await hydraulicSimulatorService.postInpAssets(file, fileName, {
			fileName,
			file: file || uploadedFile,
			locationId: context.selectedLocation,
			city_center_coordinates: '54.978, -1.6102',
			organizationId,
			alias,
			description,
		});
		return response;
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		accept: '.inp',
		showUploadList: false,
		beforeUpload: (file) => {
			setUploadedFile(file);
			return false; // Prevent auto upload
		},
		onDrop(e) {
			console.log('Dropped files', e.dataTransfer.files);
		},
	};

	const handleAddNewModel = async () => {
		if (!uploadedFile || !alias.trim()) return;

		const data = {
			name: uploadedFile.name,
			fileName: uploadedFile.name,
			file: await blobToBase64(uploadedFile),
			alias,
			description,
		};

		getMmsId(data).then((response) => {
			setSearchParams({
				mmsId: response.model_id,
				fileName: uploadedFile.name,
			});
			setSelectedFile(response);
			getTreeData(response.model_id);
			// Reset form
			setAlias('');
			setDescription('');
			setUploadedFile(null);
		});
	};

	const blobToBase64 = async (blob) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onload = () => {
				if (reader.result) {
					const result = reader.result;
					resolve(result.split(',')[1]);
				} else {
					resolve('');
				}
			};
			reader.onerror = (error) => reject(error);
		});
	};

	useEffect(() => {
		getTreeData();
		subscriptionMMS$.subscribe((event) => {
			if (event.type == 'handleGetTreeData') {
				getTreeData();
			}
		});
		return () => {};
	}, [context.selectedLocation]);

	useEffect(() => {
		if (searchParams.get('mmsId') && searchParams.get('fileName') && context.selectedLocation) {
			getModelData(searchParams.get('mmsId'), searchParams.get('fileName'));
			setSelectedFile(searchParams.get('mmsId'));
		}
	}, [searchParams.get('mmsId'), searchParams.get('fileName'), context.selectedLocation]);

	return (
		<div
			style={{
				width: '250px',
				height: 'calc(100vh - 80px)',
				backgroundColor: 'white',
				top: '0px',
				left: '0px',
				padding: '10px',
				overflow: 'auto',
				position: 'absolute',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<h4 style={{ marginBottom: '10px', textAlign: 'right' }}>Model Managment System</h4>

			{/* Upload File Section */}
			<div className="mb-4 flex flex-col justify-center items-center">
				<Upload {...uploadProps}>
					<div className="flex flex-col items-center border-2 border-dashed border-gray-300 rounded-md p-5 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
						<UploadOutlined className="text-2xl text-gray-400 mb-2" />
						<div className="text-gray-600 text-sm">Upload file</div>
					</div>
				</Upload>
				{uploadedFile && <div className="mt-2 text-xs text-blue-500">📄 {uploadedFile.name}</div>}
			</div>

			{/* Alias Field */}
			<div className="mb-4">
				<label className="block mb-1 text-sm font-medium text-left">
					Alias <span className="text-red-500">*</span>
				</label>
				<Input
					value={alias}
					onChange={(e) => setAlias(e.target.value)}
					placeholder="my_model"
					className="w-full"
				/>
			</div>

			{/* Description Field */}
			<div className="mb-4">
				<label className="block mb-1 text-sm font-medium text-left">Description</label>
				<Textarea
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder="..."
					className="w-full"
				/>
			</div>

			{/* Add New Model Button */}
			<div className="mb-5">
				<Button
					variant="primary"
					iconName={'upload'}
					label="Add New Model"
					className='w-full'
					onClick={handleAddNewModel}
					disabled={!alias.trim() || !uploadedFile}
				/>
			</div>
			<div className="flex-1 overflow-auto">
				{files && files.length > 0 ? (
					<div className="flex flex-col">
						<h4>Model versions:</h4>
						<ul style={{ padding: 0, margin: 0 }}>{renderTree(null)}</ul>
					</div>
				) : (
					<div className="text-center text-gray-500 mt-4">
						<p>No models available</p>
					</div>
				)}
			</div>
		</div>
	);
});

export default TreeFiles;
